import { Capacitor } from "@capacitor/core";
import { Device } from "@capacitor/device";
import { Network } from "@capacitor/network";
import { CommonFunctions } from "./commonUtils";
import { PushNotificationManager } from "./pushNotificationManager";

import { crashlytics } from "../services/firebase";
import { APP_VERSION } from "../version";
export interface DeviceInfo {
  // Device details
  platform: "web" | "ios" | "android";
  deviceType: "mobile" | "tablet" | "desktop";
  os: string; // e.g., "iOS 17.2", "Android 14", "macOS", "Windows"
  browser: string; // e.g., "Chrome 120", "Safari 17", "Firefox"

  // Device identification
  deviceModel?: string; // e.g., "iPhone 15 Pro", "Samsung Galaxy S23"
  deviceManufacturer?: string; // e.g., "Apple", "Samsung"
  deviceId?: string; // Device UUID (native only)

  // Screen info
  screenWidth: number;
  screenHeight: number;
  viewport: string; // e.g., "390x844"
  pixelRatio: number;

  // Network/Location
  timezone: string; // e.g., "Asia/Singapore"
  language: string; // e.g., "en-US"

  // Network info
  connectionType?: string; // wifi, cellular, none, unknown
  effectiveConnectionType?: string; // 4g, 3g, 2g, slow-2g

  // App info
  appVersion?: string;

  // User preferences
  darkMode?: boolean;
  reducedMotion?: boolean;

  // Hardware capabilities
  cpuCores?: number;
  memory?: number; // Device memory in GB
  touchSupport?: boolean;
  maxTouchPoints?: number;

  // Display
  orientation?: "portrait" | "landscape";
  displayMode?: "browser" | "standalone" | "fullscreen" | "minimal-ui";

  // Battery (native only)
  batteryLevel?: number; // 0-100
  batteryCharging?: boolean;

  // Browser capabilities
  cookiesEnabled?: boolean;
  onlineStatus?: boolean;

  // Graphics
  webglVendor?: string;
  webglRenderer?: string;

  // User agent
  userAgent: string;
}

export interface LocationInfo {
  country?: string;
  city?: string;
  region?: string;
  latitude?: number;
  longitude?: number;
  ipAddress?: string;
}

export interface PermissionSnapshot {
  location: PermissionStateValue;
  notifications: PermissionStateValue;
}

export interface SignupMetadata {
  signupMethod: "email" | "google" | "apple" | "phone" | "facebook";
  signupSource?: string; // e.g., "signup_page", "welcome_page"
  referrer?: string; // Document referrer
  deviceInfo: DeviceInfo;
  locationInfo?: Partial<LocationInfo>;
  utmParams?: {
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
  };
  permissions?: PermissionSnapshot;
}

export interface LoginMetadata {
  loginMethod: "email" | "google" | "apple" | "phone" | "facebook";
  deviceInfo: DeviceInfo;
  sessionId?: string;
}

type PermissionStateValue = "granted" | "denied" | "prompt" | "unknown" | "unsupported";

const normalizePermissionState = (
  state?: string,
): PermissionStateValue => {
  if (state === "granted" || state === "denied" || state === "prompt") {
    return state;
  }
  return state === "default" ? "prompt" : state === "unsupported" ? "unsupported" : "unknown";
};

const getLocationPermissionState = async (): Promise<PermissionStateValue> => {
  try {
    if (
      typeof navigator !== "undefined" &&
      "permissions" in navigator &&
      (navigator as any).permissions?.query
    ) {
      const status = await (navigator as any).permissions.query({
        name: "geolocation" as PermissionName,
      });
      return normalizePermissionState(status.state);
    }

    if (typeof navigator !== "undefined" && (navigator as any).geolocation) {
      return "prompt";
    }
  } catch {
    // Swallow errors; fall back to unknown below
  }

  return "unknown";
};

const getNotificationPermissionState = async (): Promise<PermissionStateValue> => {
  try {
    if (typeof Notification !== "undefined" && Notification.permission) {
      return normalizePermissionState(Notification.permission);
    }

    if (
      typeof navigator !== "undefined" &&
      "permissions" in navigator &&
      (navigator as any).permissions?.query
    ) {
      const status = await (navigator as any).permissions.query({
        name: "notifications" as PermissionName,
      });
      return normalizePermissionState(status.state);
    }

    if (Capacitor.isNativePlatform()) {
      const pushStatus = await PushNotificationManager.getStatus();
      if (pushStatus?.permissions) {
        return normalizePermissionState(pushStatus.permissions as string);
      }
    }
  } catch {
    // Swallow errors; fall back to unknown below
  }

  return "unknown";
};

export const getPermissionSnapshot = async (): Promise<PermissionSnapshot> => {
  const [location, notifications] = await Promise.all([
    getLocationPermissionState(),
    getNotificationPermissionState(),
  ]);

  return { location, notifications };
};

/**
 * Get comprehensive device information
 */
export async function getDeviceInfo(): Promise<DeviceInfo> {
  const platform = Capacitor.getPlatform() as "web" | "ios" | "android";

  if (Capacitor.isNativePlatform()) {
    // Native platform - use Capacitor Device plugin
    const info = await Device.getInfo();
    const deviceId = await Device.getId();
    const networkStatus = await Network.getStatus();

    // Get battery info (web only - Capacitor doesn't have getBatteryInfo)
    let batteryLevel: number | undefined;
    let batteryCharging: boolean | undefined;
    try {
      if ("getBattery" in navigator) {
        const battery: any = await (navigator as any).getBattery();
        batteryLevel = battery.level ? battery.level * 100 : undefined;
        batteryCharging = battery.charging;
      }
    } catch (e) {
      // Battery info not available
    }

    return {
      platform,
      deviceType:
        info.platform === "ios" || info.platform === "android"
          ? info.model?.toLowerCase().includes("ipad") ||
            info.model?.toLowerCase().includes("tablet")
            ? "tablet"
            : "mobile"
          : "desktop",
      os: `${info.operatingSystem} ${info.osVersion || ""}`.trim(),
      browser: info.webViewVersion || "Native App",
      deviceModel: info.model,
      deviceManufacturer: info.manufacturer,
      deviceId: deviceId.identifier,
      appVersion: APP_VERSION,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      pixelRatio: window.devicePixelRatio || 1,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      connectionType: networkStatus.connectionType,
      darkMode:
        window.matchMedia &&
        window.matchMedia("(prefers-color-scheme: dark)").matches,
      reducedMotion:
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches,
      cpuCores: navigator.hardwareConcurrency,
      memory: (navigator as any).deviceMemory,
      touchSupport: "ontouchstart" in window || navigator.maxTouchPoints > 0,
      maxTouchPoints: navigator.maxTouchPoints,
      orientation:
        window.innerHeight > window.innerWidth ? "portrait" : "landscape",
      displayMode: getDisplayMode(),
      batteryLevel,
      batteryCharging,
      cookiesEnabled: navigator.cookieEnabled,
      onlineStatus: navigator.onLine,
      userAgent: navigator.userAgent,
    };
  } else {
    // Web platform - use browser APIs
    const { browser, os } = parseUserAgent(navigator.userAgent);
    const connection =
      (navigator as any).connection ||
      (navigator as any).mozConnection ||
      (navigator as any).webkitConnection;
    const webgl = getWebGLInfo();

    // Get battery info (web)
    let batteryLevel: number | undefined;
    let batteryCharging: boolean | undefined;
    try {
      const battery = await (navigator as any).getBattery();
      batteryLevel = battery.level * 100;
      batteryCharging = battery.charging;
    } catch (e) {
      // Battery API not available
    }

    return {
      platform: "web",
      deviceType: getDeviceType(),
      os,
      browser,
      appVersion: APP_VERSION,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      pixelRatio: window.devicePixelRatio || 1,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      connectionType:
        connection?.type || connection?.effectiveType || "unknown",
      effectiveConnectionType: connection?.effectiveType,
      darkMode:
        window.matchMedia &&
        window.matchMedia("(prefers-color-scheme: dark)").matches,
      reducedMotion:
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches,
      cpuCores: navigator.hardwareConcurrency,
      memory: (navigator as any).deviceMemory,
      touchSupport: "ontouchstart" in window || navigator.maxTouchPoints > 0,
      maxTouchPoints: navigator.maxTouchPoints,
      orientation:
        window.innerHeight > window.innerWidth ? "portrait" : "landscape",
      displayMode: getDisplayMode(),
      batteryLevel,
      batteryCharging,
      cookiesEnabled: navigator.cookieEnabled,
      onlineStatus: navigator.onLine,
      webglVendor: webgl.vendor,
      webglRenderer: webgl.renderer,
      userAgent: navigator.userAgent,
    };
  }
}

/**
 * Get device type from user agent (web only)
 */
function getDeviceType(): "mobile" | "tablet" | "desktop" {
  const ua = navigator.userAgent.toLowerCase();

  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return "tablet";
  }

  if (
    /mobile|iphone|ipod|android|blackberry|opera mini|opera mobi|skyfire|maemo|windows phone|palm|iemobile|symbian|symbianos|fennec/i.test(
      ua,
    )
  ) {
    return "mobile";
  }

  return "desktop";
}

/**
 * Get display mode (PWA detection)
 */
function getDisplayMode():
  | "browser"
  | "standalone"
  | "fullscreen"
  | "minimal-ui" {
  if (window.matchMedia("(display-mode: fullscreen)").matches) {
    return "fullscreen";
  }
  if (window.matchMedia("(display-mode: standalone)").matches) {
    return "standalone";
  }
  if (window.matchMedia("(display-mode: minimal-ui)").matches) {
    return "minimal-ui";
  }
  return "browser";
}

/**
 * Get WebGL vendor and renderer info
 */
function getWebGLInfo(): { vendor: string; renderer: string } {
  try {
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl") || canvas.getContext("experimental-webgl");

    if (gl && gl instanceof WebGLRenderingContext) {
      const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
      if (debugInfo) {
        return {
          vendor: gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL),
          renderer: gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL),
        };
      }
    }
  } catch (e) {
    // WebGL not available
  }

  return { vendor: "Unknown", renderer: "Unknown" };
}

/**
 * Parse user agent to extract browser and OS information
 */
function parseUserAgent(ua: string): { browser: string; os: string } {
  let browser = "Unknown";
  let os = "Unknown";

  // Detect OS
  if (ua.includes("Windows NT 10.0")) os = "Windows 10";
  else if (ua.includes("Windows NT 6.3")) os = "Windows 8.1";
  else if (ua.includes("Windows NT 6.2")) os = "Windows 8";
  else if (ua.includes("Windows NT 6.1")) os = "Windows 7";
  else if (ua.includes("Mac OS X")) {
    const match = ua.match(/Mac OS X (\d+[._]\d+)/);
    os = match ? `macOS ${match[1].replace("_", ".")}` : "macOS";
  } else if (ua.includes("Android")) {
    const match = ua.match(/Android (\d+(\.\d+)?)/);
    os = match ? `Android ${match[1]}` : "Android";
  } else if (ua.includes("iPhone") || ua.includes("iPad")) {
    const match = ua.match(/OS (\d+_\d+)/);
    os = match ? `iOS ${match[1].replace("_", ".")}` : "iOS";
  } else if (ua.includes("Linux")) os = "Linux";

  // Detect Browser
  if (ua.includes("Edg/")) {
    const match = ua.match(/Edg\/([\d.]+)/);
    browser = match ? `Edge ${match[1]}` : "Edge";
  } else if (ua.includes("Chrome/") && !ua.includes("Edg")) {
    const match = ua.match(/Chrome\/([\d.]+)/);
    browser = match ? `Chrome ${match[1]}` : "Chrome";
  } else if (ua.includes("Safari/") && !ua.includes("Chrome")) {
    const match = ua.match(/Version\/([\d.]+)/);
    browser = match ? `Safari ${match[1]}` : "Safari";
  } else if (ua.includes("Firefox/")) {
    const match = ua.match(/Firefox\/([\d.]+)/);
    browser = match ? `Firefox ${match[1]}` : "Firefox";
  } else if (ua.includes("MSIE") || ua.includes("Trident/")) {
    browser = "Internet Explorer";
  }

  return { browser, os };
}

/**
 * Get approximate location from browser's geolocation API
 * Note: Requires user permission
 */
export async function getLocationInfo(): Promise<Partial<LocationInfo>> {
  if (!navigator.geolocation) {
    return {};
  }

  try {
    const position = await new Promise<GeolocationPosition>(
      (resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          timeout: 5000,
          maximumAge: 0,
        });
      },
    );

    return {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
    };
  } catch (error) {
    // User denied permission or geolocation unavailable
    return {};
  }
}

/**
 * Get location from IP address using ipapi.co service
 */
export async function getLocationFromIP(): Promise<Partial<LocationInfo>> {
  try {
    // Using ipapi.co free tier (no API key required, 1000 requests/day)
    const response = await fetch("https://ipapi.co/json/");
    const data = await response.json();

    return {
      country: data.country_name,
      city: data.city,
      region: data.region,
      latitude: data.latitude,
      longitude: data.longitude,
      ipAddress: data.ip,
    };
  } catch (error) {
    crashlytics.logError(
      `Error in getLocationFromIP: ${JSON.stringify(error?.message)}`,
      "deviceInfo.ts",
      error,
    );
    return {};
  }
}

/**
 * Generate a unique session ID
 */
export function generateSessionId(): string {
  return `${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
}

/**
 * Extract UTM parameters from URL
 */
export function getUTMParameters(): { [key: string]: string } | undefined {
  const urlParams = new URLSearchParams(window.location.search);
  const utmParams: { [key: string]: string } = {};

  const utmKeys = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
  ];
  let hasUtm = false;

  utmKeys.forEach((key) => {
    const value = urlParams.get(key);
    if (value) {
      utmParams[key] = value;
      hasUtm = true;
    }
  });

  return hasUtm ? utmParams : undefined;
}

/**
 * Get complete signup metadata
 */
export async function getSignupMetadata(
  method: "email" | "google" | "apple" | "phone" | "facebook",
  source?: string,
): Promise<SignupMetadata> {
  const deviceInfo = await getDeviceInfo();
  const locationInfo = await getLocationFromIP();
  const utmParams = getUTMParameters();
  const permissions = await getPermissionSnapshot();

  const metadata: SignupMetadata = {
    signupMethod: method,
    signupSource: source || window.location.pathname,
    deviceInfo,
    locationInfo,
    permissions,
  };

  // Only add referrer if it exists (Firestore doesn't accept undefined)
  if (document.referrer) {
    metadata.referrer = document.referrer;
  }

  // Only add UTM params if they exist
  if (utmParams) {
    metadata.utmParams = utmParams;
  }

  // Remove all undefined values before returning
  return CommonFunctions.removeUndefined(metadata);
}

/**
 * Get complete login metadata
 */
export async function getLoginMetadata(
  method: "email" | "google" | "apple" | "phone" | "facebook",
): Promise<LoginMetadata> {
  const deviceInfo = await getDeviceInfo();
  const sessionId = generateSessionId();

  const metadata: LoginMetadata = {
    loginMethod: method,
    deviceInfo,
    sessionId,
  };

  // Remove all undefined values before returning
  return CommonFunctions.removeUndefined(metadata);
}
